import React, { useState } from 'react';
import { BiPencil } from 'react-icons/bi';
import { BsCheck, BsX } from 'react-icons/bs';
import { toast } from 'react-toastify';
import AlertModal from '../../../../components/AlertModal';
import PermissaoWrapper from '../../../../components/PermissaoWrapper';
import Pessoa, { PessoaUtils, Vinculo, VinculoNome } from '../../../../model/Pessoa';
import { PermissaoEnum } from '../../../../model/Usuario/Permissao';
import PessoaViewModel from '../../../../viewmodel/PessoaViewModel';
import PessoaEditModal from '../PessoaEditModal';
import './TabelaPessoas.css';

interface TabelaPessoasProps {
  pessoas: Pessoa[];
  loading: boolean;
  onPessoaUpdated: () => void;
  vinculos: Vinculo[];
}

const TabelaPessoas: React.FC<TabelaPessoasProps> = ({ pessoas, loading, onPessoaUpdated, vinculos }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAtivarModal, setShowAtivarModal] = useState(false);
  const [showDesativarModal, setShowDesativarModal] = useState(false);
  const [selectedPessoa, setSelectedPessoa] = useState<Pessoa | null>(null);
  const [isLoadingAction, setIsLoadingAction] = useState(false);

  const handleEdit = (pessoa: Pessoa) => {
    setSelectedPessoa(pessoa);
    setShowEditModal(true);
  };

  const handleAtivar = (pessoa: Pessoa) => {
    setSelectedPessoa(pessoa);
    setShowAtivarModal(true);
  };

  const handleDesativar = (pessoa: Pessoa) => {
    setSelectedPessoa(pessoa);
    setShowDesativarModal(true);
  };

  const handleAtivarDesativar = async (pessoa: Pessoa) => {
    try {
      setIsLoadingAction(true);
      await PessoaViewModel.updatePessoaAtivo(pessoa.id, !pessoa.ativo);
      toast.success(pessoa.ativo ? 'Pessoa desativada com sucesso!' : 'Pessoa ativada com sucesso!');
      onPessoaUpdated();
      setShowAtivarModal(false);
      setShowDesativarModal(false);
      setSelectedPessoa(null);
    } catch (error) {
      toast.error('Erro ao alterar status da pessoa');
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleEditSubmit = () => {
    setShowEditModal(false);
    setSelectedPessoa(null);
    onPessoaUpdated();
  };

  const handleVinculoNome = (v: Vinculo): string => {
    return PessoaUtils.vinculoReduzido(v.nome as VinculoNome);
  };

  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-container">
          <div className="spinner-border" role="status"></div>
        </div>
      </div>
    );
  }

  if (pessoas.length === 0) {
    return (
      <div className="table-container">
        <div className="empty-state">
          <p>Nenhuma pessoa encontrada com os filtros aplicados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="table-responsive">
        <table className="pessoas-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Telefone</th>
              <th>Vínculo</th>
              <th>CPF</th>
              <th>Matrícula</th>
              <th>Status</th>
              <th style={{ textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {pessoas.map((pessoa: Pessoa) => (
              <tr key={pessoa.id}>
                <td className={!pessoa.ativo ? 'text-muted' : ''}>{pessoa.nome}</td>
                <td className={!pessoa.ativo ? 'text-muted' : ''}>{pessoa.email || 'Não informado'}</td>
                <td className={!pessoa.ativo ? 'text-muted' : ''}>{pessoa.telefone || 'Não informado'}</td>
                <td className={!pessoa.ativo ? 'text-muted' : ''}>{handleVinculoNome(pessoa.vinculo) || ''}</td>
                <td className={!pessoa.ativo ? 'text-muted' : ''}>{pessoa.cpf || 'Não informado'}</td>
                <td className={!pessoa.ativo ? 'text-muted' : ''}>{pessoa.matricula || 'Não informado'}</td>
                <td>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: pessoa.ativo ? '#d4edda' : '#f8d7da',
                      color: pessoa.ativo ? '#155724' : '#721c24',
                      padding: '6px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase'
                    }}
                  >
                    {pessoa.ativo ? 'ATIVO' : 'INATIVO'}
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ATUALIZAR_PESSOA]}>
                      <button
                        style={{
                          backgroundColor: '#e3f2fd',
                          color: 'black',
                          border: '1px solid gray',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                        title="Editar Pessoa"
                        onClick={() => handleEdit(pessoa)}
                      >
                        <BiPencil />
                      </button>
                    </PermissaoWrapper>
                    
                    {pessoa.ativo ? (
                      <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.DESATIVAR_MEMBRO]}>
                        <button
                          style={{
                            backgroundColor: '#f8d7da',
                            color: 'black',
                            border: '1px solid gray',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                          title="Desativar Pessoa"
                          onClick={() => handleDesativar(pessoa)}
                        >
                          <BsX />
                        </button>
                      </PermissaoWrapper>
                    ) : (
                      <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ATIVAR_MEMBRO]}>
                        <button
                          style={{
                            backgroundColor: '#d4edda',
                            color: 'black',
                            border: '1px solid gray',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                          title="Ativar Pessoa"
                          onClick={() => handleAtivar(pessoa)}
                        >
                          <BsCheck />
                        </button>
                      </PermissaoWrapper>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedPessoa && (
        <PessoaEditModal
          vinculos={vinculos}
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          pessoa={selectedPessoa}
          onUpdate={handleEditSubmit}
        />
      )}

      {selectedPessoa && (
        <AlertModal
          title="Ativar Pessoa"
          body={`Tem certeza que deseja ativar "${selectedPessoa.nome}"?`}
          show={showAtivarModal}
          onHide={() => setShowAtivarModal(false)}
          isLoading={isLoadingAction}
          botaoOk={{
            content: 'Ativar',
            onClick: () => handleAtivarDesativar(selectedPessoa),
            variant: 'success'
          }}
        />
      )}

      {selectedPessoa && (
        <AlertModal
          title="Desativar Pessoa"
          body={`Tem certeza que deseja desativar "${selectedPessoa.nome}"?`}
          show={showDesativarModal}
          onHide={() => setShowDesativarModal(false)}
          isLoading={isLoadingAction}
          botaoOk={{
            content: 'Desativar',
            onClick: () => handleAtivarDesativar(selectedPessoa),
            variant: 'danger'
          }}
        />
      )}
    </div>
  );
};

export default TabelaPessoas;